import type { JSX } from 'react'
import type { ShowWithPrice } from '~/shared/interfaces'
import type { BannerCardProps } from './index.h'

import { ContentBannerCardTemplate } from "/app/libraries/arcana/src/containers/RowContainer/Templates/index.ts"

import { useUnit } from 'effector-react'
import { memo, useCallback, useMemo } from 'react'
import { model as router, navigate } from '~/core/router'
import {
  getPaymentTvShowMediaType,
  model as paymentModel,
} from '~/features/payment'
import { addQuery } from '~/shared/routes'

const isRequiredShowWithPrice = (
  x: ShowWithPrice | undefined
): x is Required<ShowWithPrice> => Boolean(x && x.id != null)

export const ShowBannerCard = memo(ShowBannerCard_)

export function ShowBannerCard_(props: BannerCardProps): JSX.Element {
  const onHowToWatchClick = useUnit(paymentModel.giveItATry)
  const routeHref = useUnit(router.$href)

  const {
    item,
    favorites,
    onSignInClick,
    onFavoriteClick,
    onLockedClick,
    categoryId = 0,
    collectionId,
    onlyFavorites,
    categoryName,
  } = props

  const cardContent = item.content as ShowWithPrice | undefined

  const isCardContent = isRequiredShowWithPrice(cardContent)

  const { id, episodeCount } = isCardContent ? cardContent : { id: undefined }

  const isFavorite =
    id && favorites?.show && id in favorites.show
      ? favorites.show[id]
      : cardContent?.favorite

  const onHowToWatchClickHandler = useCallback(() => {
    if (!isCardContent) return

    onHowToWatchClick({
      entity: cardContent,
      mediaType: getPaymentTvShowMediaType(cardContent.priceSettings),
      link: `/player/series?tvshowId=${cardContent.id}`,
    })
  }, [onHowToWatchClick, cardContent, isCardContent])

  const onSignInClickHandler = useCallback(() => {
    if (!isCardContent) return

    onSignInClick?.({
      name: cardContent.name,
      redirect: routeHref ?? '',
    })
  }, [cardContent, onSignInClick, routeHref, isCardContent])

  const onFavoriteClickHandler = useCallback(() => {
    if (!isCardContent) return

    onFavoriteClick?.({
      type: 'show',
      id: cardContent.id,
      favorite: Boolean(isFavorite),
      name: cardContent.name,
    })
  }, [cardContent, onFavoriteClick, isFavorite, isCardContent])

  const onLockedClickHandler = useCallback(() => {
    if (!isCardContent) return

    const { id, locked, name } = cardContent

    onLockedClick?.({ id, locked, name })
  }, [cardContent, onLockedClick, isCardContent])

  const { playerLink, detailsLink, trailerLink } = useMemo(() => {
    const playerLink = addQuery('/player/series', {
      tvshowId: id,
      categoryName: 'Banners',
    })

    const detailsLink = addQuery(`/content/series/${id}`, {
      categoryId,
      collectionId,
      'only-favorites': onlyFavorites || undefined,
      routeName: 'Banners',
      categoryName: categoryName || 'TV Shows',
    })

    const trailerLink =
      isCardContent && cardContent.trailerPresent
        ? addQuery(playerLink, { trailer: true })
        : undefined

    return { playerLink, detailsLink, trailerLink }
  }, [
    cardContent,
    categoryId,
    categoryName,
    collectionId,
    id,
    isCardContent,
    onlyFavorites,
  ])

  const onDetailsClickHandler = useCallback(() => {
    navigate(detailsLink)
  }, [detailsLink])

  return (
    <ContentBannerCardTemplate
      {...props}
      playerLink={playerLink}
      playTitle={episodeCount ? props.playTitle : undefined}
      trailerLink={trailerLink}
      detailsLink={detailsLink}
      onDetailsClick={onDetailsClickHandler}
      onHowToWatchClick={onHowToWatchClickHandler}
      onSignInClick={onSignInClickHandler}
      onFavoriteClick={onFavoriteClickHandler}
      onLockedClick={onLockedClickHandler}
      isFavorite={isFavorite}
    />
  )
}
